import { Injectable, inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import Color from 'color';
import { cloneDeep } from 'lodash';

@Injectable({
    providedIn: 'root',
})
export class ThemeBuilderService {
    /**
     * Tint Range
     */
    private tintRange = [300, 400];

    /**
     * Shades Range
     */
    private shadesRange = [600, 700];

    /**
     * Inject Dependencies
     */
    document = inject(DOCUMENT);

    style: CSSStyleDeclaration;

    colors: { [key: string]: string } = {};

    constructor() {
        this.style = this.document.documentElement.style;
    }

    /**
     * Build Theme
     * @param colors
     */
    build(colors: any) {
        this.colors = {};
        const bgColorsSlug = ['bg-1', 'bg-2', 'bg-3'];
        this.style.setProperty(`--sb-bg-1`, colors['bg_1']);
        this.style.setProperty(`--sb-bg-2`, colors['bg_2']);
        this.style.setProperty(`--sb-bg-3`, colors['bg_3']);
        Object.keys(colors).forEach((key) => {
            if (bgColorsSlug.includes(key)) {
                return;
            }
            this.style.setProperty(`--sb-${key}-text`, this.getAdvancedContrastColor(colors[key]));
            this.colors[`--sb-${key}-text`] = this.getAdvancedContrastColor(colors[key]);
            this.colors[`--sb-${key}`] = colors[key];
            this.style.setProperty(`--sb-${key}`, colors[key]);
            this.createTints(colors[key], key);
            this.createShades(colors[key], key);
        });
    }

    /**
     * Create Shades
     * @param color
     * @param key
     */
    private createShades(color: string, key: string) {
        this.shadesRange.forEach((shade) => {
            this.colors[`--sb-${key}-${shade}`] = this.shadeColor(color, (shade - 500) / 1000);
            this.style.setProperty(
                `--sb-${key}-${shade}`,
                this.shadeColor(color, (shade - 350) / 1000),
            );
        });
    }

    /**
     * Create tints
     * @param color
     * @param key
     */
    private createTints(color: string, key: string) {
        this.tintRange.forEach((tint) => {
            this.colors[`--sb-${key}-${tint}`] = this.tintColor(color, tint / 1000);
            this.style.setProperty(`--sb-${key}-${tint}`, this.tintColor(color, tint / 1000));
        });
    }

    /**
     * Shade Color
     * @param color
     * @param percent
     */
    private shadeColor(color: string, percent: number) {
        return Color(color).darken(percent).hex();
    }

    /**
     * Tint Color
     * @param color
     * @param percent
     */
    private tintColor(color: string, percent: number) {
        return Color(color).lighten(percent).hex();
    }

    private whitenColor(color: string, percent: number) {
        return Color(color).whiten(percent).hex();
    }

    private greyscaleColor(color: string) {
        return Color(color).grayscale().hex();
    }

    private lightenColor(color: string, percent: number) {
        return Color(color).lighten(percent).hex();
    }

    // Advanced function with more detailed contrast calculation
    private getAdvancedContrastColor(bgColor: string) {
        try {
            const color = Color(bgColor);

            // Calculate WCAG contrast ratio
            const contrastRatio = (l1: number, l2: number) => {
                const lighter = Math.max(l1, l2);
                const darker = Math.min(l1, l2);
                return (lighter + 0.05) / (darker + 0.05);
            };

            const bgLuminance = color.luminosity();
            const blackContrast = contrastRatio(bgLuminance, 0.0); // Black
            const whiteContrast = contrastRatio(bgLuminance, 1.0); // White

            // WCAG recommends a contrast ratio of at least 4.5 for normal text
            const CONTRAST_THRESHOLD = 7;

            if (blackContrast >= CONTRAST_THRESHOLD) {
                return 'black';
            }

            if (whiteContrast >= CONTRAST_THRESHOLD) {
                return 'white';
            }

            // Fallback to luminance-based method if WCAG contrast is not met
            return bgLuminance > 0.5 ? 'black' : 'white';
        } catch (error) {
            console.warn('Invalid color provided:', error);
            return 'black';
        }
    }
}
